import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import { Controller, useFormContext, useFieldArray } from 'react-hook-form';

import { useMenuScopeIdentifier } from 'hooks/menuScope';
import { useOrderTypes } from 'hooks/useOrderTypes';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import TextField from '@mui/material/TextField';
import AutocompleteV2 from '@design-system/Autocomplete/AutocompleteV2';
import Chip from '@mui/material/Chip';
import IconClock from '@experimental-components/IconsComponents/Clock';
import ButtonPlus from '@experimental-components/ButtonPlus';
import IconClose from '@experimental-components/IconsComponents/Close';
import IconButton from '@design-system/IconButton';
import AlertMessage from '@experimental-components/AlertMessage';
import InputAdornmentFiled from '@design-system/InputAdornmentFiled';
import Checkbox from '@design-system/Checkbox';
import Select from '@design-system/Select';

import MenuSkeleton from 'components/MenuForm/MenuSkeleton';
import MultiSelect from 'components/MultiSelect';
import SkuMultiStore from 'components/SkuMultiStore';

import {
  formDefinition,
  getWeekDaysSelected,
  getInitialOptionsToMultiSelect,
  getWeekendDaysSelected,
  validateUppercase,
  handleFormatValidateItems,
  validateDuplicateSkuItems,
  getProvidersValues,
} from 'utils/menuMaker';
import { optionsTimes } from 'utils/times';
import { regexTypes, formatTextAllowedValues } from 'utils/formatField';

import CheckBoxChanel from './CheckBoxChanel';
import useStyles from './styles';

/**
 * MenuForm Component
 * Renders the menu creation/edition form with scope-based restrictions
 * Only shows the "Edit menu items" section when scope is STORE
 *
 * @param {Object} props - Component props
 * @param {Object} props.categoriesState - Categories state including list and fetching status
 * @param {boolean} props.focusInCategories - Whether to focus on categories section
 * @param {boolean} props.isEdition - Whether this is editing an existing menu
 * @param {Object} props.menuState - Menu state including data and fetching status
 * @param {Function} props.onSetStep - Function to change form step
 * @param {Function} props.t - Translation function
 * @returns {React.ReactElement} Menu form component
 */
function MenuForm({ categoriesState, focusInCategories, isEdition, menuState, onSetStep, t }) {
  const categoriesRef = useRef(null);
  const classes = useStyles();
  const { control, errors, register, setValue, watch, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: 'timeFields' });
  const { isStoreMode, isOrganizationMode } = useMenuScopeIdentifier();
  const { orderTypes, orderTypesState } = useOrderTypes();
  const watchFields = watch();
  const [selectedWeekDays, setSelectedWeekDays] = useState([]);
  const [selectedWeekendDays, setSelectedWeekendDays] = useState([]);

  // Disable sections when scope is STORE, but keep them rendered
  const shouldDisableMenuBasicSections = isStoreMode;

  useEffect(() => {
    setSelectedWeekDays(getWeekDaysSelected(fields));
    setSelectedWeekendDays(getWeekendDaysSelected(fields));
  }, [fields]);

  useEffect(() => {
    if (focusInCategories) {
      categoriesRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [categoriesRef.current]);

  const handleRemoveSchedule = (index) => {
    const selectedWeekDaysTmp = selectedWeekDays.filter((item, indexSelected) => indexSelected !== index);
    const selectedWeekendDaysTmp = selectedWeekendDays.filter((item, indexSelected) => indexSelected !== index);

    setSelectedWeekDays(selectedWeekDaysTmp);
    setSelectedWeekendDays(selectedWeekendDaysTmp);
    remove(index);
  };

  const handleSelectAllWeekDays = (isChecked, index) => {
    const selectedWeekDaysTmp = JSON.parse(JSON.stringify(selectedWeekDays));
    selectedWeekDaysTmp[index] = [];

    formDefinition.weekdaysListCheckBox.forEach((weekDay) => {
      if (isChecked) selectedWeekDaysTmp[index].push(weekDay?.value);

      setValue(`timeFields[${index}].${weekDay?.name}`, isChecked ? weekDay?.value : false, { shouldValidate: true });
    });

    setSelectedWeekDays(selectedWeekDaysTmp);
  };

  const hanldeVerifyCheckedCheckBox = (array, value) => {
    return array?.indexOf(value) > -1;
  };

  const handleSelectWeekDay = (event, index) => {
    const selectedWeekDaysTmp = JSON.parse(JSON.stringify(selectedWeekDays));
    const selectedWeekDaysTmpIndex = JSON.parse(JSON.stringify(selectedWeekDaysTmp[index])).filter(
      (day) => day !== event.target.value,
    );

    if (event.target.checked) selectedWeekDaysTmpIndex.push(event.target.value);

    selectedWeekDaysTmp[index] = selectedWeekDaysTmpIndex;
    setSelectedWeekDays(selectedWeekDaysTmp);
  };

  const handleSelectAllWeekend = (isChecked, index) => {
    const selectedWeekendDaysTmp = JSON.parse(JSON.stringify(selectedWeekendDays));
    selectedWeekendDaysTmp[index] = [];

    formDefinition.weekendListCheckBox.forEach((weekendDay) => {
      if (isChecked) selectedWeekendDaysTmp[index].push(weekendDay?.value);

      setValue(`timeFields[${index}].${weekendDay?.name}`, isChecked ? weekendDay?.value : false, {
        shouldValidate: true,
      });
    });

    setSelectedWeekendDays(selectedWeekendDaysTmp);
  };

  const handleSelectWeekendDay = (event, index) => {
    const selectedWeekendDaysTmp = JSON.parse(JSON.stringify(selectedWeekendDays));
    const selectedWeekendDaysTmpIndex = JSON.parse(JSON.stringify(selectedWeekendDaysTmp[index])).filter(
      (day) => day !== event.target.value,
    );

    if (event.target.checked) selectedWeekendDaysTmpIndex.push(event.target.value);

    selectedWeekendDaysTmp[index] = selectedWeekendDaysTmpIndex;
    setSelectedWeekendDays(selectedWeekendDaysTmp);
  };

  const appendFormTimer = () => {
    setSelectedWeekDays([...selectedWeekDays, []]);
    setSelectedWeekendDays([...selectedWeekendDays, []]);
    append({});
  };

  const handleValidateAllowedValues = (target, field) => {
    target.value = formatTextAllowedValues(target.value, regexTypes.REGEX_PRODUCT_NAME_ALLOWED);
    setValue(field, target.value);
    return target.value;
  };

  const handleOpenSelectSKU = () => {
    onSetStep(2);
  };

  const renderTitle = () => {
    return (
      <Typography className={classes.title}>{t(`menuMaker:sections.${isEdition ? 'editMenu' : 'addMenu'}`)}</Typography>
    );
  };

  if (categoriesState.fetching || menuState.fetching) {
    return (
      <MenuSkeleton>
        <MenuSkeleton.Title>{renderTitle()}</MenuSkeleton.Title>
      </MenuSkeleton>
    );
  }

  if (isEdition ? menuState.data && categoriesState.fetched : categoriesState.fetched) {
    return (
      <Grid container direction="column" spacing={3}>
        <Grid item>{renderTitle()}</Grid>
        {isStoreMode && (
          <Box pl={3} pt={3}>
            <AlertMessage severity="info" sizeIcon={20}>
              <Box>
                <Trans i18nKey={t(`menuMaker:headerForm.store.notifications.info`)} />
              </Box>
            </AlertMessage>
          </Box>
        )}
        <Grid item>
          <InputAdornmentFiled
            {...formDefinition.nameMenuField}
            autoComplete="off"
            autoFocus
            data-testid="inputNameMenu"
            disabled={shouldDisableMenuBasicSections}
            endAdornment={
              <InputAdornment position="end">
                <Box color="#CECDD1" fontSize="14px">
                  {watch('nameMenuField')?.length ?? 0}/30
                </Box>
              </InputAdornment>
            }
            error={Boolean(errors[formDefinition.nameMenuField.name])}
            errorMsg={t(errors[formDefinition.nameMenuField.name]?.message, { chars: 30 })}
            fullWidth
            inputProps={{ maxLength: formDefinition.nameMenuField.maxLength }}
            inputRef={register({
              ...formDefinition.nameMenuField.register,
              validate: {
                controlUppercase: (value) => validateUppercase(value) || t('common:errors.controlUppercase'),
              },
            })}
            label={t(formDefinition.nameMenuField.label)}
            onChange={({ target }) => handleValidateAllowedValues(target, 'inputNameMenu')}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.subtitles}>{t('menuMaker:createMenu.schedules.title')}</Typography>

          <Box mb={3}>
            <Typography color="textSecondary" variant="body2">
              {t('menuMaker:createMenu.schedules.header')}
            </Typography>
          </Box>

          <Grid container direction="column">
            {fields.map((field, index) => (
              <Grid item key={field.id}>
                <Box className={classes.paperHeader}>
                  {index !== 0 && (
                    <IconButton
                      data-testid="btnRemoveSchedule"
                      disabled={shouldDisableMenuBasicSections}
                      onClick={() => handleRemoveSchedule(index)}
                      size="large"
                    >
                      <IconClose lighter size={20} />
                    </IconButton>
                  )}
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      as={
                        <Select
                          {...formDefinition.startDateField}
                          data-testid="selectStartDate"
                          disabled={shouldDisableMenuBasicSections}
                          fullWidth
                          icon={<IconClock />}
                          label={t(formDefinition.startDateField.label)}
                        >
                          {optionsTimes.map((time) => (
                            <MenuItem key={time.value} value={time.value}>
                              {time.label}
                            </MenuItem>
                          ))}
                        </Select>
                      }
                      control={control}
                      defaultValue={field?.startDateField || ''}
                      error={Boolean(errors?.timeFields?.length && errors?.timeFields[index]?.startDateField)}
                      helperText={t(errors?.timeFields?.length && errors?.timeFields[index]?.startDateField?.message)}
                      name={`timeFields[${index}].${formDefinition.startDateField.name}`}
                      rules={{ ...formDefinition.startDateField.rules }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Controller
                      as={
                        <Select
                          {...formDefinition.endDateField}
                          data-testid="selectEndDate"
                          disabled={shouldDisableMenuBasicSections}
                          fullWidth
                          icon={<IconClock />}
                          label={t(formDefinition.endDateField.label)}
                          onClick={() => trigger()}
                        >
                          {optionsTimes.map((time) => (
                            <MenuItem key={time.value} value={time.value}>
                              {time.label}
                            </MenuItem>
                          ))}
                        </Select>
                      }
                      control={control}
                      defaultValue={field?.endDateField || ''}
                      error={Boolean(errors?.timeFields?.length && errors?.timeFields[index]?.endDateField)}
                      helperText={t(errors?.timeFields?.length && errors?.timeFields[index]?.endDateField?.message)}
                      name={`timeFields[${index}].${formDefinition.endDateField.name}`}
                      rules={{ ...formDefinition.endDateField.rules }}
                    />
                  </Grid>
                </Grid>

                <Box mt={2}>
                  <Grid className={classes.weekdaysContainer} container>
                    <Grid item>
                      <Checkbox
                        checked={selectedWeekDays[index]?.length === 5}
                        className={classes.checkBoxSelectAll}
                        data-testid="weekDaysCheck"
                        disabled={shouldDisableMenuBasicSections}
                        indeterminate={selectedWeekDays[index]?.length === 5}
                        label={t('menuMaker:createMenu.fields.weekday')}
                        onChange={({ target: { checked } }) => handleSelectAllWeekDays(checked, index)}
                      />

                      <FormControl component="fieldset" variant="standard">
                        <FormGroup row>
                          {formDefinition.weekdaysListCheckBox.map((weekdaysCheck) => (
                            <Checkbox
                              {...weekdaysCheck}
                              checked={hanldeVerifyCheckedCheckBox(selectedWeekDays[index], weekdaysCheck.value)}
                              className={classes.weekdays}
                              data-testid={weekdaysCheck.name}
                              disabled={shouldDisableMenuBasicSections}
                              inputRef={register()}
                              key={weekdaysCheck.name}
                              label={t(weekdaysCheck.label)}
                              name={`timeFields[${index}].${weekdaysCheck.name}`}
                              onChange={(e) => handleSelectWeekDay(e, index)}
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    </Grid>

                    <Grid item>
                      <Checkbox
                        checked={selectedWeekendDays[index]?.length === 2}
                        className={classes.checkBoxSelectAll}
                        data-testid="weekendDaysCheck"
                        disabled={shouldDisableMenuBasicSections}
                        indeterminate={selectedWeekendDays[index]?.length === 2}
                        label={t('menuMaker:createMenu.fields.weekend')}
                        onChange={(e) => handleSelectAllWeekend(e.target.checked, index)}
                      />

                      <FormControl component="fieldset" variant="standard">
                        <FormGroup row>
                          {formDefinition.weekendListCheckBox.map((weekendCheck) => (
                            <Checkbox
                              {...weekendCheck}
                              checked={hanldeVerifyCheckedCheckBox(selectedWeekendDays[index], weekendCheck.value)}
                              className={classes.weekdays}
                              data-testid={weekendCheck.name}
                              disabled={shouldDisableMenuBasicSections}
                              inputRef={register()}
                              key={weekendCheck.name}
                              label={t(weekendCheck.label)}
                              name={`timeFields[${index}].${weekendCheck.name}`}
                              onChange={(e) => handleSelectWeekendDay(e, index)}
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                {index + 1 !== fields.length ? (
                  <Box my={4}>
                    <Divider />
                  </Box>
                ) : null}
              </Grid>
            ))}
          </Grid>

          <Box mt={3}>
            <ButtonPlus
              data-testid="btnAddSchedule"
              disabled={shouldDisableMenuBasicSections}
              onClick={appendFormTimer}
            >
              {t('menuMaker:createMenu.schedules.buttonAdd')}
            </ButtonPlus>
          </Box>

          <Box mt={4}>
            <Divider />
          </Box>
        </Grid>

        <Grid item ref={categoriesRef}>
          <Box>
            <Typography className={classes.subtitles}>{t('menuMaker:createMenu.categories.title')}</Typography>

            <Box mb={1}>
              <Typography color="textSecondary" variant="body2">
                {t('menuMaker:createMenu.categories.inputPlaceholder')}
              </Typography>
            </Box>

            <Grid container>
              <Grid item xs={12}>
                <Box
                  sx={{
                    pointerEvents: shouldDisableMenuBasicSections ? 'none' : 'auto',
                    opacity: shouldDisableMenuBasicSections ? 0.5 : 1,
                  }}
                >
                  <Controller
                    control={control}
                    name="selectedCategories"
                    render={({ onChange, value, name }) => (
                      <MultiSelect
                        getOptionLabel={(option) => option.name}
                        getOptionUniqueKey={(option) => option.uuid}
                        inputPlaceholderText={t('menuMaker:createMenu.categories.inputPlaceholder')}
                        name={name}
                        noOptionsText={t('menuMaker:createMenu.categories.noCategories')}
                        onChange={(selectedOptions) => onChange(handleFormatValidateItems(selectedOptions))}
                        options={getInitialOptionsToMultiSelect(categoriesState.list, value)}
                        selectedOptions={value}
                        value={value}
                      />
                    )}
                    rules={{
                      validate: (items) => {
                        const itemsDuplicateSku = validateDuplicateSkuItems(items);

                        return (
                          itemsDuplicateSku?.length === 0 || t('menuMaker:skuLabels.menu.errorDuplicateCategoriesSku')
                        );
                      },
                    }}
                  />
                  {Boolean(errors?.selectedCategories) && (
                    <Box className={classes.errorMessagesCustom} mt={2}>
                      {errors?.selectedCategories?.message}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box mb={1} mt={4}>
            <Divider />
          </Box>
        </Grid>

        <Grid item>
          <Typography className={classes.subtitles}>{t('menuMaker:createMenu.chanels.title')}</Typography>

          <Box>
            <Typography color="textSecondary" variant="body2">
              {t('menuMaker:createMenu.chanels.header')}
            </Typography>
          </Box>

          <Box mt={3}>
            <FormControl component="fieldset" fullWidth variant="standard">
              <FormGroup className={classes.formChanel}>
                {formDefinition.providersOptions.map((providerOption) => (
                  <CheckBoxChanel
                    data-testid={`provider-option-${providerOption.id}`}
                    defaultChecked={Boolean(watchFields[providerOption.name])}
                    disabled={shouldDisableMenuBasicSections}
                    inputRef={register({ ...providerOption.register })}
                    key={providerOption.name}
                    label={t(providerOption.label)}
                    name={providerOption.name}
                    value={providerOption.value}
                  />
                ))}
              </FormGroup>
            </FormControl>
            {getProvidersValues(watchFields)?.length === 0 && (
              <Box className={classes.errorMessagesCustom} mt={0}>
                {t('menuMaker:createMenu.fieldErrors.selectProviders')}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item>
          <Typography className={classes.subtitles}>{t('menuMaker:createMenu.editMenuProducts.title')}</Typography>

          <Box mb={3}>
            <Typography color="textSecondary" variant="body2">
              {t('menuMaker:createMenu.editMenuProducts.header')}
            </Typography>
          </Box>

          {isEdition ? (
            <Box display="grid" gap={3}>
              <Controller
                control={control}
                name={formDefinition.changePricesField.name}
                render={({ onChange, value, name }) => (
                  <ToggleButtonGroup
                    className={classes.toggleOverride}
                    exclusive
                    name={name}
                    onChange={(e, option) => option !== null && onChange(option)}
                    value={value}
                  >
                    <ToggleButton data-testid="radioChangePriceYes" value={1}>
                      {t('common:options.yes')}
                    </ToggleButton>
                    <ToggleButton value={0}>{t('common:options.no')}</ToggleButton>
                  </ToggleButtonGroup>
                )}
              />

              <Typography className={classes.grayBox} variant="body2">
                <Trans i18nKey="menuMaker:createMenu.editMenuProducts.editionInfo" />
              </Typography>
            </Box>
          ) : (
            <Typography className={classes.grayBox} variant="body2">
              {t('menuMaker:createMenu.editMenuProducts.editionNotAvailable')}
            </Typography>
          )}

          <Box mb={1} mt={4}>
            <Divider />
          </Box>
        </Grid>

        {!isOrganizationMode && (
          <Grid item>
            <Typography className={classes.subtitles}>{t('menuMaker:createMenu.orderTypes.title')}</Typography>

            <Box mb={3} mt={1}>
              <Typography color="textSecondary" variant="body2">
                {t('menuMaker:createMenu.orderTypes.header')}
              </Typography>
            </Box>
            <Box mb={1} mt={0}>
              <Typography color="testEnv" variant="body2">
                {t('menuMaker:createMenu.orderTypes.subTitle')}
              </Typography>
            </Box>
            <Controller
              control={control}
              defaultValue={[]}
              name="selectedOrderTypes"
              render={({ onChange, value }) => {
                return (
                  <AutocompleteV2
                    data-testid="orderTypesAutocomplete"
                    disabled={orderTypesState.fetching}
                    getOptionLabel={(option) => option.name}
                    hideLabel
                    isOptionEqualToValue={(option, selectedValue) => option.uuid === selectedValue.uuid}
                    label=""
                    loading={orderTypesState.fetching}
                    multiple
                    noOptionsText={t('menuMaker:createMenu.orderTypes.noOptions')}
                    onChange={(event, newValue) => {
                      onChange(newValue);
                    }}
                    options={orderTypes}
                    placeholder={t('menuMaker:createMenu.orderTypes.placeholder')}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip
                          label={option.name}
                          {...getTagProps({ index })}
                          data-testid={`order-type-chip-${option.uuid}`}
                          key={option.uuid}
                        />
                      ))
                    }
                    value={value || []}
                  />
                );
              }}
            />

            <Box mb={1} mt={4}>
              <Divider />
            </Box>
          </Grid>
        )}

        <Grid item>
          <Typography className={classes.subtitles}>{t('menuMaker:skuLabels.key')}</Typography>

          <Box mb={4} mt={1}>
            <Typography color="textSecondary" variant="body2">
              {t('menuMaker:skuLabels.menu.info')}
            </Typography>
          </Box>

          <Box
            sx={{
              pointerEvents: shouldDisableMenuBasicSections ? 'none' : 'auto',
              opacity: shouldDisableMenuBasicSections ? 0.5 : 1,
            }}
          >
            <SkuMultiStore
              errors={errors}
              onClickButton={handleOpenSelectSKU}
              prefix="ME-"
              register={register}
              trigger={trigger}
              watch={watch}
            />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return null;
}

MenuForm.propTypes = {
  categoriesState: PropTypes.object,
  focusInCategories: PropTypes.bool,
  isEdition: PropTypes.bool,
  menuState: PropTypes.object,
  onSetStep: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation('menuMaker')(MenuForm);
